import { useEffect, useRef, useState } from 'react'
import type {
  CaseEndpoint,
  ImportEndpointItem,
  ImportEndpointsRequest,
} from '../../../../types/case-container'
import { listEndpoints } from '../../../../services/endpoints'
import { logger } from '../../../../utils/logger'
import { type BlockingError, type FixableWarning, parseCsvContent } from '../csv-import.utils'

const SEPARATOR_LENGTH = 60
const VERIFICATION_RECORDS_LIMIT = 10

export type ImportStep =
  | 'select'
  | 'preview'
  | 'confirm-fixes'
  | 'importing'
  | 'success'
  | 'blocking-errors'

export interface ImportResult {
  imported: number
  skipped: number
  errors: { row: number; field: string; message: string }[]
}

interface UseCsvImportModalArgs {
  caseId: string
  onImport: (data: ImportEndpointsRequest) => Promise<ImportResult | null>
}

export interface CsvImportModalState {
  step: ImportStep
  file: File | null
  preview: ImportEndpointItem[]
  allParsedEndpoints: ImportEndpointItem[]
  blockingErrors: BlockingError[]
  fixableWarnings: FixableWarning[]
  error: string | null
  result: ImportResult | null
  verificationRecords: CaseEndpoint[]
  loadingVerification: boolean
  fileInputRef: React.RefObject<HTMLInputElement>
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  handleImport: () => Promise<void>
  handleReset: () => void
  downloadErrorLog: () => void
}

function buildErrorLogText(file: File | null, blockingErrors: BlockingError[]): string {
  const lines: string[] = [
    'Infrastructure Import Error Log',
    `File: ${file?.name ?? 'Unknown'}`,
    `Date: ${new Date().toISOString()}`,
    `Total Errors: ${blockingErrors.length.toString()}`,
    '',
    '='.repeat(SEPARATOR_LENGTH),
    '',
  ]
  blockingErrors.forEach((err, index) => {
    lines.push(
      `Error ${(index + 1).toString()}:`,
      `  Row: ${err.row.toString()}`,
      `  Field: ${err.field}`,
      `  Value: "${err.value}"`,
      `  Message: ${err.message}`,
      ''
    )
  })
  return lines.join('\n')
}

function triggerErrorLogDownload(text: string): void {
  const blob = new Blob([text], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `import_errors_${new Date().toISOString().split('T')[0] ?? ''}.txt`
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

async function readFileTextSafely(
  file: File,
  setBlockingErrors: React.Dispatch<React.SetStateAction<BlockingError[]>>,
  setStep: React.Dispatch<React.SetStateAction<ImportStep>>
): Promise<string | null> {
  try {
    return await file.text()
  } catch {
    setBlockingErrors([
      {
        row: 0,
        field: 'file',
        value: file.name,
        message: 'Failed to read CSV file - the file may be corrupted or in an invalid format',
      },
    ])
    setStep('blocking-errors')
    return null
  }
}

async function loadExistingHostnames(caseId: string): Promise<Set<string>> {
  try {
    const existing = await listEndpoints(caseId)
    return new Set(existing.map((ep) => ep.hostname.toLowerCase()))
  } catch (err) {
    logger.error('CaseInfrastructureTab', '[CSV Import] Failed to fetch existing endpoints:', err)
    return new Set<string>()
  }
}

interface ParsedSetters {
  setPreview: React.Dispatch<React.SetStateAction<ImportEndpointItem[]>>
  setAllParsedEndpoints: React.Dispatch<React.SetStateAction<ImportEndpointItem[]>>
  setBlockingErrors: React.Dispatch<React.SetStateAction<BlockingError[]>>
  setFixableWarnings: React.Dispatch<React.SetStateAction<FixableWarning[]>>
  setStep: React.Dispatch<React.SetStateAction<ImportStep>>
}

function applyParseResult(
  parsed: ReturnType<typeof parseCsvContent>,
  setters: ParsedSetters
): void {
  if ('fatalError' in parsed) {
    setters.setBlockingErrors([parsed.fatalError])
    setters.setStep('blocking-errors')
    return
  }
  setters.setPreview(parsed.preview)
  setters.setAllParsedEndpoints(parsed.parsed)
  setters.setBlockingErrors(parsed.blockingErrors)
  setters.setFixableWarnings(parsed.fixableWarnings)
  if (parsed.blockingErrors.length > 0) setters.setStep('blocking-errors')
  else if (parsed.fixableWarnings.length > 0) setters.setStep('confirm-fixes')
  else setters.setStep('preview')
}

interface CsvImportStateBag {
  step: ImportStep
  setStep: React.Dispatch<React.SetStateAction<ImportStep>>
  file: File | null
  setFile: React.Dispatch<React.SetStateAction<File | null>>
  preview: ImportEndpointItem[]
  setPreview: React.Dispatch<React.SetStateAction<ImportEndpointItem[]>>
  allParsedEndpoints: ImportEndpointItem[]
  setAllParsedEndpoints: React.Dispatch<React.SetStateAction<ImportEndpointItem[]>>
  blockingErrors: BlockingError[]
  setBlockingErrors: React.Dispatch<React.SetStateAction<BlockingError[]>>
  fixableWarnings: FixableWarning[]
  setFixableWarnings: React.Dispatch<React.SetStateAction<FixableWarning[]>>
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  result: ImportResult | null
  setResult: React.Dispatch<React.SetStateAction<ImportResult | null>>
  verificationRecords: CaseEndpoint[]
  setVerificationRecords: React.Dispatch<React.SetStateAction<CaseEndpoint[]>>
  loadingVerification: boolean
  setLoadingVerification: React.Dispatch<React.SetStateAction<boolean>>
  fileInputRef: React.RefObject<HTMLInputElement>
}

function useCsvImportState(): CsvImportStateBag {
  const [step, setStep] = useState<ImportStep>('select')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportEndpointItem[]>([])
  const [allParsedEndpoints, setAllParsedEndpoints] = useState<ImportEndpointItem[]>([])
  const [blockingErrors, setBlockingErrors] = useState<BlockingError[]>([])
  const [fixableWarnings, setFixableWarnings] = useState<FixableWarning[]>([])
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [verificationRecords, setVerificationRecords] = useState<CaseEndpoint[]>([])
  const [loadingVerification, setLoadingVerification] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  return {
    step,
    setStep,
    file,
    setFile,
    preview,
    setPreview,
    allParsedEndpoints,
    setAllParsedEndpoints,
    blockingErrors,
    setBlockingErrors,
    fixableWarnings,
    setFixableWarnings,
    error,
    setError,
    result,
    setResult,
    verificationRecords,
    setVerificationRecords,
    loadingVerification,
    setLoadingVerification,
    fileInputRef,
  }
}

function useCsvLifecycleLogging(step: ImportStep, result: ImportResult | null): void {
  useEffect(() => {
    logger.debug('CaseInfrastructureTab', '[CSV Import Modal] State changed:', {
      step,
      hasResult: !!result,
      resultData: result,
    })
  }, [step, result])
  useEffect(() => {
    logger.debug('CaseInfrastructureTab', '[CSV Import Modal] Component MOUNTED')
    return () => {
      logger.debug('CaseInfrastructureTab', '[CSV Import Modal] Component UNMOUNTED')
    }
  }, [])
}

function makeFetchVerificationRecords(
  caseId: string,
  state: CsvImportStateBag
): () => Promise<void> {
  return async (): Promise<void> => {
    state.setLoadingVerification(true)
    try {
      const endpoints = await listEndpoints(caseId, { importSource: 'csv' })
      const sorted = [...endpoints].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      )
      state.setVerificationRecords(sorted.slice(0, VERIFICATION_RECORDS_LIMIT))
    } catch (err) {
      logger.error(
        'CaseInfrastructureTab',
        '[CSV Import] Failed to fetch verification records:',
        err
      )
    } finally {
      state.setLoadingVerification(false)
    }
  }
}

function makeHandleFileChange(
  caseId: string,
  state: CsvImportStateBag
): (e: React.ChangeEvent<HTMLInputElement>) => Promise<void> {
  return async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    if (!selectedFile.name.endsWith('.csv')) {
      state.setError('Please select a CSV file')
      return
    }
    state.setFile(selectedFile)
    state.setError(null)
    state.setResult(null)
    state.setBlockingErrors([])
    state.setFixableWarnings([])
    state.setVerificationRecords([])
    state.setStep('select')
    const existingHostnames = await loadExistingHostnames(caseId)
    const text = await readFileTextSafely(selectedFile, state.setBlockingErrors, state.setStep)
    if (text === null) return
    const setters: ParsedSetters = {
      setPreview: state.setPreview,
      setAllParsedEndpoints: state.setAllParsedEndpoints,
      setBlockingErrors: state.setBlockingErrors,
      setFixableWarnings: state.setFixableWarnings,
      setStep: state.setStep,
    }
    try {
      const parsed = parseCsvContent({ text, fileName: selectedFile.name, existingHostnames })
      applyParseResult(parsed, setters)
    } catch {
      state.setBlockingErrors([
        {
          row: 0,
          field: 'file',
          value: selectedFile.name,
          message: 'Failed to parse CSV file - the file may be corrupted or in an invalid format',
        },
      ])
      state.setStep('blocking-errors')
    }
  }
}

interface ImportActionDeps {
  state: CsvImportStateBag
  onImport: (data: ImportEndpointsRequest) => Promise<ImportResult | null>
  fetchVerificationRecords: () => Promise<void>
}

function makeHandleImport(deps: ImportActionDeps): () => Promise<void> {
  const { state, onImport, fetchVerificationRecords } = deps
  return async (): Promise<void> => {
    if (state.allParsedEndpoints.length === 0) return
    logger.debug(
      'CaseInfrastructureTab',
      `[CSV Import] Starting import of ${state.allParsedEndpoints.length.toString()} endpoints`
    )
    state.setStep('importing')
    try {
      const importResult = await onImport({ source: 'csv', endpoints: state.allParsedEndpoints })
      if (importResult) {
        state.setResult(importResult)
        await fetchVerificationRecords()
        state.setStep('success')
      } else {
        state.setError('Import failed - no response from server')
        state.setStep('preview')
      }
    } catch (err) {
      logger.error('CaseInfrastructureTab', '[CSV Import] Error:', err)
      state.setError('Failed to import CSV file')
      state.setStep('preview')
    }
  }
}

function makeHandleReset(state: CsvImportStateBag): () => void {
  return (): void => {
    state.setStep('select')
    state.setFile(null)
    state.setPreview([])
    state.setAllParsedEndpoints([])
    state.setBlockingErrors([])
    state.setFixableWarnings([])
    state.setError(null)
    state.setResult(null)
    state.setVerificationRecords([])
    if (state.fileInputRef.current) state.fileInputRef.current.value = ''
  }
}

export function useCsvImportModal(args: UseCsvImportModalArgs): CsvImportModalState {
  const { caseId, onImport } = args
  const state = useCsvImportState()
  useCsvLifecycleLogging(state.step, state.result)
  const fetchVerificationRecords = makeFetchVerificationRecords(caseId, state)
  const downloadErrorLog = (): void => {
    triggerErrorLogDownload(buildErrorLogText(state.file, state.blockingErrors))
  }
  const handleFileChange = makeHandleFileChange(caseId, state)
  const handleImport = makeHandleImport({ state, onImport, fetchVerificationRecords })
  const handleReset = makeHandleReset(state)
  return {
    step: state.step,
    file: state.file,
    preview: state.preview,
    allParsedEndpoints: state.allParsedEndpoints,
    blockingErrors: state.blockingErrors,
    fixableWarnings: state.fixableWarnings,
    error: state.error,
    result: state.result,
    verificationRecords: state.verificationRecords,
    loadingVerification: state.loadingVerification,
    fileInputRef: state.fileInputRef,
    handleFileChange,
    handleImport,
    handleReset,
    downloadErrorLog,
  }
}
